import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TreatmentEntity } from './interfaces/treatment-entity';
import { TreatmentType } from './enums';
import { TreatmentModel } from './models/treatment-model';
import { AppointmentModel } from './models/appointment-model';
import { PriceListCode } from './models/price-list-code';
import { environment } from 'src/environments/environment';
import moment from 'moment';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { SelectListItem } from './models/select-list-item';
import { StartOptions } from '@openreplay/tracker/dist/lib/main/app';


@Injectable()
export class Utils {


    /**  return array ['08:00', '08:30', '09:00', '09:30'] */
    static generateTimeList(min: string, max: string, step: number, includeLast: boolean): SelectListItem[] {
        const myArray: SelectListItem[] = [];
        const minDate = new Date(`2020-01-01T${min}`);
        const maxDate = max.indexOf('1.00:00') > -1 ? new Date(`2020-01-02T00:00`) : new Date(`2020-01-01T${max}`);
       
        for (let currentDate = new Date(minDate); currentDate <= maxDate; currentDate.setMinutes(currentDate.getMinutes() + step)) {
             var currentTimeText = currentDate.toTimeString().substring(0, 5); // get time from '14:27:55 GMT+0200 (Israel Standard Time)''            
             var currentTimeValue = currentDate.toTimeString().substring(0, 5);
             
             if(!includeLast && currentDate.getTime() == maxDate.getTime()){
                 break;
             }
             if(currentDate.getTime() > maxDate.getTime()){
                 break;
             }
             if(currentDate.getDate() - minDate.getDate() > 0){
                 myArray.push(new SelectListItem({ Text:currentTimeText, Value: '1.00:00' }) ); // end of day
             }else{
                 myArray.push(new SelectListItem({ Text:currentTimeText, Value: currentTimeValue }) );
             }
           
        }
        return myArray;
    }

    /** return a number with 2 decimal digits 4 => 4.00 */
    static toDecimal(value) {
        const val = +value || 0; // the (+) sign converts to a number
        return val.toFixed(2);
    }

    /** get the time h:mm */
    static getTime(date: Date): string {
        var hours = date.getHours();
        var minutes = date.getMinutes();
        return `${hours}:${minutes}`;
    }

    static generateVisitReasonSummary1(treatments: TreatmentEntity[], translate: TranslateService, abbreviate: boolean = false): string {
        /*
            for 1 treatment             return 'treatmentCode'
            for 1 treatment from series return 'treatmentCode (series)'
            for 1 treatment from plan   return 'treatmentCode (plan)'            
            more then 1 treatment                return 'total treatments';
            more then 1 treatment from series    return 'total (series)';
            more then 1 treatment from plan      return 'total (plan)';
        */

        if (!treatments || treatments.length === 0) {
            return '';
        }

        if (treatments.length === 1) {
            const treatment = treatments[0];
            const name = treatment.Name || '';
            const parentType = treatment.ParentTypeID;
            let suffix = '';
            switch (parentType) {
                case TreatmentType.Series:
                    suffix = translate.instant('SERIES');
                    return `${name} (${suffix})`; // return CodeName (Series)
                case TreatmentType.Plan:
                    suffix = translate.instant('PLAN');
                    return `${name} (${suffix})`; // return CodeName (Plan)
                default:
                    return name;
            }
        }

        for (let index = 0; index < treatments.length; index++) {
            const treatment = treatments[index];
            const parentType = treatment.ParentTypeID;
            let suffix = '';
            switch (parentType) {
                case TreatmentType.Series:
                    suffix = translate.instant('SERIES');
                    break;
                case TreatmentType.Plan:
                    suffix = translate.instant('PLAN');
                    break;
                default:
                    suffix = translate.instant(abbreviate ? 'TREATMENT.ABBREVIATE.TREATMENTS' : 'TREATMENTS');
                    break;
            }
            return `${treatments.length} (${suffix})`; // 2 (Treatments) | 2 (Series) | 2 (Plan), 
        }
    }

    static generateVisitReasonSummary2(model: AppointmentModel, translate: TranslateService): string {

        /*
            for 1 treatment             return 'treatmentCode'
            for 1 treatment from series return 'treatmentCode (series)'
            for 1 treatment from plan   return 'treatmentCode (plan)'
            
            more then 1 treatment                return 'total treatments';
            more then 1 treatment from series    return 'total (series)';
            more then 1 treatment from plan      return 'total (plan)';

        */

        if (!model) {
            return '';
        }

        const parentType = model.TreatmentType;
        const allTreatment = JSON.parse(model.TreatmentsFromCard) as TreatmentModel[];
        const treatments = allTreatment.filter(x => x.Selected);

        if (!treatments || treatments.length === 0) {
            return '';
        }

        if (treatments.length === 1) {
            const treatment = treatments[0];
            const name = treatment.TreatmentCodeName;
            let suffix = '';
            switch (parentType) {
                case TreatmentType.Series:
                    suffix = translate.instant('SERIES');
                    return `${name} (${suffix})`; // return CodeName (Series)
                case TreatmentType.Series:
                    suffix = translate.instant('PLAN');
                    return `${name} (${suffix})`; // return CodeName (Plan)
                default:
                    return name;
            }
        }

        for (let index = 0; index < treatments.length; index++) {
            const treatment = treatments[index];
            let suffix = '';
            switch (parentType) {
                case TreatmentType.Series:
                    suffix = translate.instant('SERIES');
                    break;
                case TreatmentType.Plan:
                    suffix = translate.instant('PLAN');
                    break;
                default:
                    suffix = translate.instant('TREATMENTS');
                    break;
            }
            return `${treatments.length} (${suffix})`; // 2 (Treatments) | 2 (Series) | 2 (Plan), 
        }
    }

    static generateVisitReasonSummary3(items: PriceListCode[], translate: TranslateService): string {
        /*
            for 1 treatment             return 'treatmentCode'
            for 1 treatment from series return 'treatmentCode (series)'
            for 1 treatment from plan   return 'treatmentCode (plan)'            
            more then 1 treatment                return 'total treatments';
            more then 1 treatment from series    return 'total (series)';
            more then 1 treatment from plan      return 'total (plan)';
        */
        if (!items || items.length === 0) {
            return '';
        }

        if (items.length === 1) {
            const pricelistCode = items[0];
            const name = pricelistCode && pricelistCode.Name || '';
            const parentType = pricelistCode.ParentTypeID;
            let suffix = '';
            switch (parentType) {
                case TreatmentType.Series:
                    suffix = translate.instant('SERIES');
                    return `${name} (${suffix})`; // return CodeName (Series)
                case TreatmentType.Series:
                    suffix = translate.instant('PLAN');
                    return `${name} (${suffix})`; // return CodeName (Plan)
                default:
                    return name;
            }
        }

        for (let index = 0; index < items.length; index++) {
            const pricelistCode = items[index];
            const parentType = pricelistCode.ParentTypeID;
            let suffix = '';
            switch (parentType) {
                case TreatmentType.Series:
                    suffix = translate.instant('SERIES');
                    break;
                case TreatmentType.Plan:
                    suffix = translate.instant('PLAN');
                    break;
                default:
                    suffix = translate.instant('TREATMENTS');
                    break;
            }
            return `${items.length} (${suffix})`; // 2 (Treatments) | 2 (Series) | 2 (Plan), 
        }

    }

    static priceListCodeToString(item: PriceListCode, translate: TranslateService): string {

        if (!item) {
            return '';
        }

        let msg = item.Name;

        if (item.Duration > 0) {
            const minutes = translate.instant('TIME.MINS');
            msg = `${item.Name} ${item.Duration} ${minutes}`;
        }
        if (item.ParentTypeID === TreatmentType.Series || item.SubCodeID > 0) {
            msg += ` (${translate.instant('SERIES')})`;
        }

        return msg;
    }

    static treatmentToString(item: TreatmentEntity, translate: TranslateService): string {
        if (!item) {
            return '';
        }

        const minutesLabel = translate.instant('TIME.MINS');
        const duration = item.PriceListCode && item.PriceListCode.Duration || 0;
        const msg = `${item.Name == null ? item.PriceListCode.Name : item.Name} ${duration} ${minutesLabel}`;
        if (item.TreatmentParent) {
            const parentType = item.TreatmentParent.TypeID;
            const parentName = item.TreatmentParent.Name;
            if (parentType === TreatmentType.Series) {
                const seriesLabel = translate.instant('SERIES');
                return `${msg} (${seriesLabel})`;
            } else if (parentType === TreatmentType.Plan) {
                const planLabel = translate.instant('PLAN');
                return `${msg} (${planLabel} ${parentName})`;
            }
        }
        return msg;
    }

    static getApiEndPoint(): string {
        
        if (environment.production || environment.staging || environment.hotfix) {

            // console.log('environment: ' + environment.name);

            const endpoint = environment.APIEndpoint; // https://{0}.drweb-sys.com/api/
            // console.log('endpoint: ' + endpoint);

            const full = window.location.host; // test.m-staging.drweb-sys.com
            // console.log('window.location.host: ' + full);

            const parts = full.split('.');
            // console.log('parts: ' + JSON.stringify(parts));

            const fixedEndPoint = endpoint.replace('{0}', parts[0]);
            // console.log('fixedEndPoint: ' + fixedEndPoint);

            return fixedEndPoint;

        } else {            
            return environment.APIEndpoint; // for beta
        }
    }


    static pad(num: number, size: number): string {
        let s = num + '';
        while (s.length < size) s = '0' + s;
        return s;
    }


    static IsValidIsraeliID(val: string) {
        val = val && val.trim() || '';
        const mishkal = '121212121';
        // empty is valid
        if (val === '') {
            return true;
        }
        const id = val.padStart(9, '0'); // 123 => 000000123
        let sum = 0;
        for (let i = 0; i < 9; i++) {

            if (isNaN(+id[i])) { // the (+) sign convert string to number 
                return false;
            }
            const digit = parseInt(id[i]);
            const n = parseInt(mishkal[i]); // 1 or 2
            const result = digit * n; // d * 1||2
            const result2 = result < 10 ? result : Math.trunc(result / 10) + (result % 10); // 12 => 1+2 => 3
            sum += result2;
        }
        let isValid = sum % 10 == 0;
        return isValid;
    }

    static isStringBlank(str: string) {
        return (!str || /^\s*$/.test(str));
    }

    static isDatePassed(date: moment.Moment) {
        const today = moment().startOf('day');
        const start = moment(date).startOf('day');
        const days = today.diff(start, 'days');
        return days > 0;
    }

    static isFutureDate(date: moment.Moment) {
        const today = moment().startOf('day');
        const start = moment(date).startOf('day');
        const days = start.diff(today, 'days');
        return days > 0;
    }


    static getFormValidationErrors(form: UntypedFormGroup): string {
        var errors = [];
        Object.keys(form.controls).forEach(key => {
            const controlErrors: ValidationErrors = form.get(key).errors;
            if (controlErrors != null) {
                Object.keys(controlErrors).forEach(keyError => {
                    errors.push('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', JSON.stringify(controlErrors[keyError]));
                });
            }
        });

        return errors.join(", ")
    }

    static isMobile(){        
        var userAgent = navigator.userAgent;
        return (!userAgent.includes('Windows') && !userAgent.includes('Macintosh'));
    }

    static dispatchEvent(el: HTMLElement, action){
        // action = click | mouseup | ...
        try{
            el.dispatchEvent(new MouseEvent(action, {bubbles: true, cancelable: true, view: window}));
        }
        catch(err){
            // let event = new Event('MouseEvents');
            // event.initEvent('click', true, false);
            // el.dispatchEvent(event);
        }      
        
    }

    static getOpenReplayDefaultSettings() :any{
        var env = environment;
        return {    
            projectKey: env.openReplayKey,
            ingestPoint: env.openReplayIngestPoint,
            __DISABLE_SECURE_MODE: env.name.toLocaleLowerCase() == 'development',
            defaultInputMode: 0,
            obscureTextNumbers: false,
            obscureTextEmails: true,
            crossOrigin: true,
            captureIFrames: true,
            capturePerformance: true,
            crossdomain: {
                enabled: true,
                parentDomain: '*',
            },
            onStart: ({ sessionID }) => console.log("OpenReplay tracker started with session: ", sessionID),
        };
    }

    // the userID is the user logging to open replay we do not need to pass anything in here.
    static getOpenReplayStartOptions():StartOptions{
        return {
            userID: '',
            metadata: {
              subdomain: window.location.hostname.split('.')[0] 
            } 
          };
    }

    static getOpenReplayTrackAssistsOptions(): any{
        return {
            name: 'trackerAssist',
            onAgentConnect: ({ email, name, query }) => {
              console.log(`Agent ${name} (${email}) connected with query:`, query);
        
              return function onAgentDisconnect() {
                console.log(`Agent ${name} disconnected.`);
              };
            },
            onCallStart: ({ email, name, query }) => {
              console.log(`Call started with ${name} (${email})`);
        
              return function onCallEnd() {
                console.log(`Call with ${name} ended.`);
              };
            },
            onRemoteControlStart: ({ email, name, query }) => {
              console.log(`Remote control started with ${name} (${email})`);
        
              return function onRemoteControlEnd() {
                console.log(`Remote control with ${name} ended.`);
              };
            },
            onRecordingStart: () => {
                console.log("Session recording started.");
              },
            
              onRecordingStop: () => {
                console.log("Session recording stopped.");
              },
            
              onRequestUserMedia: ({ mediaType }) => {
                console.log(`User media requested: ${mediaType}`);
              },
            
              onAgentMessage: ({ email, name, message }) => {
                console.log(`Agent ${name} (${email}) sent a message: ${message}`);
              },
            
              onError: (error) => {
                console.error("Assist plugin error:", error);
              }
          };
    }


}